/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { groupBy, last, orderBy, uniqBy } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "commodities.default";
const MONTH_FREQUENCY = 4;

export function useHighchartTransformer(olapDataPrice: OLAPTransformerResult | null, olapDataIndex: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarParameter] = useParameter<number[]>("@calendar");
  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");
  const [crisesItems] = useDictionary(dictionaryService, "slides23-crises");

  const olapColumns = useMemo(() => {
    const allColumns = [olapDataPrice, olapDataIndex].filter(Boolean).flatMap(e => e?.columns);
    const uniqColumns = uniqBy(allColumns, e => e?.selection[CATEGORIES_AXIS]).filter(Boolean);
    const primarySortedColumns = orderBy(uniqColumns, e => e?.selection[CATEGORIES_AXIS]);
    const secondarySortedColumns = orderBy(primarySortedColumns, e => String(e?.selection[CATEGORIES_AXIS]).slice(1, 5));
    return secondarySortedColumns as Column[];
  }, [olapDataPrice, olapDataIndex]);

  const [monthesCategories] = useMemo(() =>
    [MONTH_FREQUENCY]
      .map(frequency => {
        const frequencyColumns = olapColumns.filter(colum => {
          const colFrequency = +String(colum.selection[CATEGORIES_AXIS])[0];
          return colFrequency === frequency;
        });
        const frequencyCalendarIds = calendarParameter?.filter(calendarId => {
          const calendarFrequency = Number(String(calendarId)[0]);
          return calendarFrequency === frequency;
        });
        const lastColumn = frequencyColumns[frequencyColumns.length - 1];
        const lastColIndex = frequencyCalendarIds?.indexOf(lastColumn?.selection[CATEGORIES_AXIS] as number) || 0;
        const ignoreCalendarIds = frequencyCalendarIds?.slice(lastColIndex + 1) || [];

        return frequencyCalendarIds?.filter(calendarId => !ignoreCalendarIds.includes(calendarId)).map(calendarId => {
          const calendarItem = calendarItems?.find(item => item.id === String(calendarId));
          const name = calendarItem?.getValue<string>("name") || '';
          return {
            calendarId, name
          };
        }) || []
      })
    , [olapColumns, calendarItems, calendarParameter]
  );

  const allRows = [olapDataPrice, olapDataIndex].flatMap(data => data?.rows);
  const allPoints = allRows.flatMap(row => row?.points).filter(Boolean);
  const maxPointValue = Math.max(...allPoints.map(p => p!.value));
  const yMax = Math.ceil(maxPointValue / 100) * 100;

  const options = useChartOptions({
    tooltip: {
      useHTML: true,
      formatter: function () {
        const { category, serieName, unit, crises, startDate, endDate } = this.point as any;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        if (crises) {
          return `<div>
              <b>${serieName || this.point.series.name}</b><br/>
              <b>${startDate} - ${endDate}</span>
            </div>`;
        }
        return `<div>
            <b>${serieName || this.point.series.name}</b><br/>
            <b>${category}</b>: <span>${pointValue} ${unit}</span>
        </div>`;
      },
      outside: true,
      style: {
        zIndex: 100,
      },
    },
  }, [
    chartPipe("spline"),
    legendPipe("bottom", {
      useHTML: true,
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(monthesCategories.map(category => category.name), {
      visible: true,
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          return label;
        }
      },

    }),
    addYAxisPipe({
      max: yMax,
      gridLineColor: '#fff',
      title: {
        text: "oil price & crop price index",
      },
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapDataPrice && olapDataIndex) {
      const colors = ['#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'];
      const commoditiesSeries: SeriesOptionsType[] = olapDataPrice.rows.map((row, rowIndex) => {
        let commodityName = row.attributes[`${SERIES_AXIS}.name`];

        if (typeof commodityName === 'object') {
          commodityName = commodityName[9] || [];
        }

        const serieName = `${commodityName} Price`;
        return {
          name: `${commodityName} Price ($/ton)`,
          type: 'spline',
          data: monthesCategories?.map((category, categoryIndex) => {
            const point = row.points.find(p => p.column.selection[CATEGORIES_AXIS] === category.calendarId);
            const value = point?.value || null;
            return { x: categoryIndex, y: value, date: category.name, serieName, unit: '$/ton' };
          }) || [],
          yAxis: 0,
          xAxis: 0,
          color: colors[rowIndex],
          zIndex: 2,
          marker: {
            radius: 0,
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          dataLabels: {
            enabled: false,
          }
        }
      });

      const indexSerie: SeriesOptionsType = {
        name: 'Crop Price Index*',
        type: 'spline',
        data: monthesCategories?.map((category, categoryIndex) => {
          const row = olapDataIndex.rows[0];
          const point = row.points.find(p => p.column.selection[CATEGORIES_AXIS] === category.calendarId);
          const value = point?.value || null;
          const isLast = categoryIndex === monthesCategories.length - 1;
          return { x: categoryIndex, y: value, date: category.name, isLast, unit: '%' };
        }) || [],
        yAxis: 0,
        xAxis: 0,
        color: '#0F8AC0',
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: false,
        },
      }

      const crises = crisesItems?.map(crisesItem => {
        const startDate = crisesItem.getValue<number>('start_date') || 0;
        const endDate = crisesItem.getValue<number>('end_date') || 0;
        const name = crisesItem.getValue<object>('name')![9];
        return {
          name,
          startDate,
          endDate,
        };
      });

      const crisesGroupsRecords = groupBy(crises, item => `${item.startDate}-${item.endDate}`);
      const crisesGroups = Object.keys(crisesGroupsRecords).map(key => crisesGroupsRecords[key]);

      const crisesSeries: SeriesOptionsType[] = crisesGroups?.map(crisesGroup => {
        const startDate = crisesGroup[0].startDate;
        const endDate = crisesGroup[0].endDate;
        const name = crisesGroup.map(e => e.name).filter(Boolean).join(', ');
        const serieCategories = monthesCategories.filter(c => c.calendarId >= startDate && c.calendarId <= endDate);
        const startCalendar = calendarItems?.find(e => e.id === String(startDate));
        const endCalendar = calendarItems?.find(e => e.id === String(endDate));

        const startDateName = startCalendar?.getValue<any>('name');
        const endDateName = endCalendar?.getValue<any>('name');

        const data = monthesCategories.map((month, categoryIndex) => {
          const y = serieCategories.includes(month) ? yMax - 1 : null;
          return { x: categoryIndex, y, crises: true, startDate: startDateName, endDate: endDateName };
        });


        return {
          type: 'area',
          showInLegend: false,
          zIndex: 1,
          dataLabels: {
            enabled: false,
          },
          marker: {
            radius: 0,
          },
          name,
          data,
          color: '#ffb6b6',
        };
      }) || [];

      const series: SeriesOptionsType[] = [...commoditiesSeries, indexSerie, ...crisesSeries];
      const result: Highcharts.Options = {
        ...options,
        series,
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapDataPrice, olapDataIndex, olapColumns, monthesCategories, crisesItems, calendarItems]);
  return state;
}
